// One voicemail drop library (a workspace number's, or one person's direct line): load, add (WAV through the
// greeting path's checks), rename, reorder, set the default and delete. Every change is sent once and then the
// library is read again, so what is shown is the server's order and default, never a local guess. A failed
// change is returned to the panel; nothing is retried.
import { isAbortError, type VoicemailDropOwner, type VoicemailDropsClient } from '../../lib/api.ts';
import {
  DROP_LIMITS, type Result, type VoicemailDrop, type VoicemailFault, applyMove, dropFault, isDropsMissing, movedIndex, readLibrary, readOne, rememberAudio, reorderPatches, unreadableDrops,
} from './drops.ts';
import { isRoleRefusal } from './model.ts';

export interface LibrarySnapshot {
  status: 'idle' | 'loading' | 'ready' | 'error';
  drops: VoicemailDrop[];
  limit: number;
  skipped: number;
  error: VoicemailFault | null;
  /** The server refused the read (403). */
  denied: boolean;
  /** The server has no drop routes yet. */
  missing: boolean;
  /** The server refused a change (403): changes turn off. */
  manageDenied: boolean;
  /** Ids whose change is in flight. */
  busy: ReadonlySet<string>;
}

export type DropsApi = Pick<VoicemailDropsClient, 'list' | 'create' | 'update' | 'remove' | 'makeDefault'>;

export class DropLibraryStore {
  #state: LibrarySnapshot = { status: 'idle', drops: [], limit: DROP_LIMITS.perLibrary, skipped: 0, error: null, denied: false, missing: false, manageDenied: false, busy: new Set() };
  readonly #listeners = new Set<() => void>();
  #token = 0;
  #abort: AbortController | null = null;

  constructor(private readonly client: DropsApi, readonly owner: VoicemailDropOwner) {}

  getSnapshot = (): LibrarySnapshot => this.#state;
  subscribe = (listener: () => void): (() => void) => {
    this.#listeners.add(listener);
    return () => this.#listeners.delete(listener);
  };
  #set(change: (s: LibrarySnapshot) => LibrarySnapshot): void {
    this.#state = change(this.#state);
    for (const l of [...this.#listeners]) l();
  }
  dispose(): void {
    this.#abort?.abort();
  }

  async load(): Promise<void> {
    const token = ++this.#token;
    this.#abort?.abort();
    const abort = new AbortController();
    this.#abort = abort;
    this.#set(s => ({ ...s, status: 'loading', error: null }));
    try {
      const read = readLibrary(await this.client.list(this.owner, abort.signal));
      if (token !== this.#token) return;
      if (!read) throw unreadableDrops();
      this.#set(s => ({ ...s, status: 'ready', drops: read.drops, limit: read.limit, skipped: read.skipped, denied: false, missing: false }));
    } catch (error) {
      if (isAbortError(error) || token !== this.#token) return;
      const fault = dropFault(error, 'Couldn’t load the voicemail drops');
      this.#set(s => ({ ...s, status: 'error', error: fault, denied: isRoleRefusal(fault), missing: isDropsMissing(fault) }));
    }
  }

  #busy(id: string, on: boolean): void {
    this.#set(s => {
      const busy = new Set(s.busy);
      if (on) busy.add(id);
      else busy.delete(id);
      return { ...s, busy };
    });
  }

  async #change<T>(key: string, work: () => Promise<T>, title: string): Promise<Result<T>> {
    // A change in flight makes any earlier read stale; the change reads the library again itself.
    this.#token += 1;
    this.#busy(key, true);
    try {
      return { ok: true, value: await work() };
    } catch (error) {
      const fault = dropFault(error, title);
      if (isRoleRefusal(fault)) this.#set(s => ({ ...s, manageDenied: true }));
      // Stale, gone or moved by someone else: show the server's library again.
      if (fault.status === 404 || fault.status === 409) void this.load();
      return { ok: false, fault };
    } finally {
      this.#busy(key, false);
    }
  }

  /** POST raw canonical WAV with the name. The new drop joins the end of the library (the first one becomes the default). */
  add(name: string, wav: Uint8Array<ArrayBuffer>): Promise<Result<VoicemailDrop>> {
    return this.#change('new', async () => {
      const drop = readOne(await this.client.create(this.owner, name.replace(/\s+/g, ' ').trim(), wav));
      if (!drop) throw unreadableDrops();
      rememberAudio(drop.id, wav);
      await this.load();
      return drop;
    }, 'Couldn’t add the voicemail drop');
  }

  rename(drop: VoicemailDrop, name: string): Promise<Result<VoicemailDrop | null>> {
    return this.#change(drop.id, async () => {
      const next = readOne(await this.client.update(drop.id, { name: name.replace(/\s+/g, ' ').trim(), expected_version: drop.version ?? 1 }));
      await this.load();
      return next;
    }, 'Couldn’t rename the voicemail drop');
  }

  /**
   * Moves one place up (-1) or down (+1). The list shows the move at once; each drop whose place changed is sent its
   * new sort order (one PATCH each, in order, against its own version), then the server's order replaces the guess.
   */
  move(drop: VoicemailDrop, delta: -1 | 1): Promise<Result<null>> {
    const to = movedIndex(this.#state.drops, drop.id, delta);
    if (to === null) return Promise.resolve({ ok: true, value: null });
    const before = this.#state.drops;
    const after = applyMove(before, drop.id, to);
    this.#set(s => ({ ...s, drops: after }));
    return this.#change(drop.id, async () => {
      for (const patch of reorderPatches(before, after)) await this.client.update(patch.drop.id, { sort_order: patch.sort_order, expected_version: patch.drop.version ?? 1 });
      await this.load();
      return null;
    }, 'Couldn’t move the voicemail drop').then(result => {
      // A move is up to two PATCHes, so one may have landed: put the old list back, then read the server's.
      if (!result.ok && result.fault.status !== 404 && result.fault.status !== 409) {
        if (this.#state.drops === after) this.#set(s => ({ ...s, drops: before }));
        void this.load();
      }
      return result;
    });
  }

  makeDefault(drop: VoicemailDrop): Promise<Result<null>> {
    return this.#change(drop.id, async () => {
      await this.client.makeDefault(drop.id);
      await this.load();
      return null;
    }, 'Couldn’t change the default voicemail drop');
  }

  /** DELETE; 404 means it was already gone, which reads the same as deleted. */
  remove(drop: VoicemailDrop): Promise<Result<null>> {
    return this.#change(drop.id, async () => {
      try {
        await this.client.remove(drop.id);
      } catch (error) {
        const fault = dropFault(error, 'Couldn’t delete the voicemail drop');
        if (!(fault.status === 404 && fault.code === 'not_found')) throw fault;
      }
      this.#set(s => ({ ...s, drops: s.drops.filter(d => d.id !== drop.id) }));
      await this.load();
      return null;
    }, 'Couldn’t delete the voicemail drop');
  }
}
